import React, { useEffect, useState, useCallback } from 'react';
import { getAllSmsMessages, sendBulkSms } from '../../services/SmsService';
import { getAllSmsTemplates } from '../../services/TemplatesService';
import { Link } from 'react-router-dom';
import SmsHistorySidebar from './SmsHistorySidebar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Pagination from '../../utils/Pagination';

// Custom debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

function SmsMessages() {
  const [smsMessages, setSmsMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarSms, setSidebarSms] = useState(null);
  const [newSmsModalOpen, setNewSmsModalOpen] = useState(false);
  
  // New SMS form state
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  
  // Search and sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDescending, setSortDescending] = useState(true);

  // Filter sidebar state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    recipientNumbers: '',
    senderNumbers: '',
    status: '',
    messageBody: '',
    dateFrom: '',
    dateTo: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    recipientNumbers: '',
    senderNumbers: '',
    status: '',
    messageBody: '',
    dateFrom: '',
    dateTo: '',
  });

  // Sync tempFilters when sidebar opens or appliedFilters change
  useEffect(() => {
    setTempFilters({ ...appliedFilters });
  }, [isFilterOpen, appliedFilters]);

  // Count active filters
  const activeFilterCount = Object.values(appliedFilters).filter(v => v !== '').length;

  // Debounce the search term
  const debouncedSearchTerm = useDebounce(searchTerm, 2000);

  const fetchSmsMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        pageNumber: currentPage,
        pageSize,
        searchTerm: debouncedSearchTerm,
        sortBy,
        sortDescending,
      };

      if (appliedFilters.recipientNumbers) params.recipientNumbers = appliedFilters.recipientNumbers;
      if (appliedFilters.senderNumbers) params.senderNumbers = appliedFilters.senderNumbers;
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.messageBody) params.messageBody = appliedFilters.messageBody;
      if (appliedFilters.dateFrom) params.dateFrom = appliedFilters.dateFrom;
      if (appliedFilters.dateTo) params.dateTo = appliedFilters.dateTo;

      const data = await getAllSmsMessages(params);
      setSmsMessages(data.items || []);
      setTotalItems(data.totalCount || 0);
    } catch (err) {
      setError('Failed to load SMS messages');
      setSmsMessages([]);
      setTotalItems(0);
    }
    setLoading(false);
  }, [currentPage, pageSize, debouncedSearchTerm, sortBy, sortDescending, appliedFilters]);

  useEffect(() => {
    fetchSmsMessages();
  }, [fetchSmsMessages]);

  // Fetch templates when modal opens
  useEffect(() => {
    if (newSmsModalOpen) {
      fetchTemplates();
    }
  }, [newSmsModalOpen]);

  const fetchTemplates = async () => {
    try {
      const data = await getAllSmsTemplates();
      setTemplates(data);
    } catch (err) {
      toast.error('Failed to load SMS templates');
    }
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    const template = templates.find(t => String(t.id) === String(templateId));
    if (template) {
      setMessage(template.message || '');
    } else {
      setMessage('');
    }
  };

  const handleViewHistory = (sms) => {
    setSidebarSms(sms);
    setSidebarOpen(true);
  };

  const openBlockModal = (phoneNumber, isBlocked) => {
    if (isBlocked) {
      toast.info(`Unblock Phone Number: ${phoneNumber} (This is in progress)`, { position: 'top-right', autoClose: 2500 });
    } else {
      toast.success(`Block Phone Number: ${phoneNumber} (This is in progress)`, { position: 'top-right', autoClose: 2500 });
    }
  };

  const getStatusBadgeClass = (status = '') => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'bg-success';
      case 'sent':
      case 'processed':
      case 'created':
        return 'bg-secondary';
      case 'failed':
      case 'bounce':
      case 'deferred':
        return 'bg-danger';
      case 'undelivered':
        return 'bg-warning text-dark';
      default:
        return 'bg-dark text-white';
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(true);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // Filter handlers
  const handleTempFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const handleClearAllFilters = () => {
    const emptyFilters = {
      recipientNumbers: '',
      senderNumbers: '',
      status: '',
      messageBody: '',
      dateFrom: '',
      dateTo: '',
    };
    setTempFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  const handleNewSmsSubmit = async (e) => {
    e.preventDefault();
    
    if (!to.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const phoneNumbers = to.split(',').map(num => num.trim()).filter(num => num);
      await sendBulkSms({ to: phoneNumbers, message });
      toast.success('SMS sent successfully!');
      setNewSmsModalOpen(false);
      setTo('');
      setMessage('');
      setSelectedTemplateId('');
      fetchSmsMessages();
    } catch (error) {
      toast.error('Failed to send SMS');
    }
  };

  return (
    <div style={{ paddingLeft: 25, paddingRight: 25, width: '100%' }} className="mt-4">
      <ToastContainer />
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">SMS Messages</h4>
        <div className="d-flex gap-2">
         
          <button
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={() => setIsFilterOpen(true)}
            disabled={loading}
            title="Filter"
          >
            <i className="bi bi-funnel-fill me-2"></i>
            Filter
            {activeFilterCount > 0 && (
              <span className="badge bg-primary ms-1">{activeFilterCount}</span>
            )}
          </button>
          <button
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={fetchSmsMessages}
            disabled={loading}
            title="Refresh SMS"
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-arrow-clockwise me-2"></i>
            )}
            Refresh
          </button>
          <Link to="/new-sms" className="btn btn-primary d-flex align-items-center">
            <i className="bi bi-pencil-square me-2"></i>
            New SMS
          </Link>
        </div>
      </div>

      {/* ==================== FILTER SIDEBAR ==================== */}
      {/* Overlay */}
      <div
        className={`position-fixed top-0 end-0 bottom-0 start-0 bg-dark bg-opacity-50 ${!isFilterOpen ? "d-none" : ""}`}
        onClick={() => setIsFilterOpen(false)}
        style={{ zIndex: 1040 }}
      />

      {/* Sidebar */}
      <div
        className={`position-fixed top-0 end-0 bottom-0 bg-white shadow overflow-auto ${!isFilterOpen ? "translate-end d-none" : ""}`}
        style={{ width: "500px", zIndex: 1045 }}
      >
        <div className="d-flex flex-column h-100">
          {/* Header */}
          <div className="p-3 border-bottom bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-primary">
                <i className="bi bi-funnel-fill me-2"></i>Filter SMS Messages
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setIsFilterOpen(false)}
                aria-label="Close"
              />
            </div>
          </div>

          {/* Filter Fields */}
          <div className="p-3 flex-grow-1">
            {/* Recipient Number */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Recipient Numbers</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by recipient number..."
                  name="recipientNumbers"
                  value={tempFilters.recipientNumbers}
                  onChange={handleTempFilterChange}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Sender Number */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Sender Numbers</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by sender number..."
                  name="senderNumbers"
                  value={tempFilters.senderNumbers}
                  onChange={handleTempFilterChange}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Message Body */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Message</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by message content..."
                  name="messageBody"
                  value={tempFilters.messageBody}
                  onChange={handleTempFilterChange}
                  disabled={loading}
                />
              </div>
            </div>

            <hr className="my-4" />

            <div className="vstack gap-4">
              {/* Status */}
              <div>
                <label className="form-label fw-bold small text-muted mb-2">Status</label>
                <select
                  className="form-select"
                  name="status"
                  value={tempFilters.status}
                  onChange={handleTempFilterChange}
                  disabled={loading}
                >
                  <option value="">All</option>
                  <option value="delivered">Delivered</option>
                  <option value="sent">Sent</option>
                  <option value="processed">Processed</option>
                  <option value="created">Created</option>
                  <option value="failed">Failed</option>
                  <option value="undelivered">Undelivered</option>
                  <option value="bounce">Bounce</option>
                  <option value="deferred">Deferred</option>
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="form-label fw-bold small text-muted mb-2">Date Range</label>
                <div className="mb-3">
                  <label className="form-label small text-muted">From</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
                    <input
                      type="date"
                      className="form-control"
                      name="dateFrom"
                      value={tempFilters.dateFrom}
                      onChange={handleTempFilterChange}
                      disabled={loading}
                    />
                  </div>
                </div>
                <div>
                  <label className="form-label small text-muted">To</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
                    <input
                      type="date"
                      className="form-control"
                      name="dateTo"
                      value={tempFilters.dateTo}
                      onChange={handleTempFilterChange}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-top bg-light">
            <div className="d-grid gap-2">
              <button
                className="btn btn-outline-secondary"
                onClick={handleClearAllFilters}
                disabled={loading}
              >
                <i className="bi bi-x-circle me-1"></i>Clear All Filters
              </button>
              <button
                className="btn btn-primary"
                onClick={handleApplyFilters}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>Search / Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* ==================== END FILTER SIDEBAR ==================== */}

      {/* SMS Messages Table */}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>
                    <button 
                      className="btn btn-link text-decoration-none text-dark p-0"
                      onClick={() => handleSort('recipientNumber')}
                    >
                      Recipient {sortBy === 'recipientNumber' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>
                    <button 
                      className="btn btn-link text-decoration-none text-dark p-0"
                      onClick={() => handleSort('senderNumber')}
                    >
                      Sender {sortBy === 'senderNumber' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>
                    <button 
                      className="btn btn-link text-decoration-none text-dark p-0"
                      onClick={() => handleSort('body')}
                    >
                      Message {sortBy === 'body' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>Status</th>
                  <th>
                    <button 
                      className="btn btn-link text-decoration-none text-dark p-0"
                      onClick={() => handleSort('createdAt')}
                    >
                      Date {sortBy === 'createdAt' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(smsMessages) && smsMessages.map(sms => (
                  <tr key={sms.id}>
                    <td>{sms.recipientNumber}</td>
                    <td>{sms.senderNumber}</td>
                    <td>
                      <div title={sms.body} style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                        {sms.body}
                      </div>
                    </td>
                    <td>
                      <span className={`badge text-capitalize ${getStatusBadgeClass(sms.lastStatus)}`}>{sms.lastStatus || 'Unknown'}</span>
                    </td>
                    <td>{new Date(sms.createdAt).toLocaleString()}</td>
                    <td>
                      <div className="btn-group">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleViewHistory(sms)}
                          title="View History"
                        >
                          <i className="bi bi-clock-history"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => openBlockModal(sms.recipientNumber, false)}
                          title="Block/Unblock"
                        >
                          <i className="bi bi-shield-x"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="d-flex align-items-center">
              <select 
                className="form-select form-select-sm me-2" 
                value={pageSize} 
                onChange={handlePageSizeChange}
                style={{ width: 'auto' }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
              <span className="text-muted">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
              </span>
            </div>
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        </>
      )}

      <button
        className="btn btn-primary rounded-circle position-fixed"
        style={{
          bottom: '2rem',
          right: '2rem',
          width: '3.5rem',
          height: '3.5rem',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          zIndex: 1000
        }}
        onClick={() => setNewSmsModalOpen(true)}
        title="Send New SMS"
      >
        <i className="bi bi-chat-dots-fill" style={{ fontSize: '1.5rem' }}></i>
      </button>

      {/* New SMS Modal */}
      {newSmsModalOpen && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send New SMS</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setNewSmsModalOpen(false)}
                ></button>
              </div>
              <form onSubmit={handleNewSmsSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Select a Template</label>
                    <select className="form-select" value={selectedTemplateId} onChange={handleTemplateChange}>
                      <option value="">-- No Template --</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.templateName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="to" className="form-label fw-semibold">Phone Numbers (comma-separated) *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="to"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      placeholder="+1234567890, +0987654321"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="message" className="form-label fw-semibold">Message *</label>
                    <textarea
                      className="form-control"
                      id="message"
                      rows="4"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Enter your SMS message..."
                      required
                      maxLength="160"
                    ></textarea>
                    <small className="text-muted">
                      <span className={message.length > 140 ? 'text-warning' : 'text-muted'}>
                        {message.length}/160 characters
                      </span>
                      {message.length > 140 && (
                        <span className="text-warning ms-2">
                          (Message may be split into multiple SMS)
                        </span>
                      )}
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setNewSmsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Send SMS
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* SMS History Sidebar */}
      <SmsHistorySidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sms={sidebarSms}
      />
    </div>
  );
}

export default SmsMessages;